/**
 * Menu Service
 * Handles loading, parsing, and filtering of menu structure
 */

import menuStructure from '../../../docs/navigation/phase1/menu_structure.json';

// Type Definitions
export interface MenuPermission {
  screen: string;
  actions: string[];
}

export interface MenuScreen {
  id: string;
  label: string;
  description: string;
  route: string;
  icon: string;
  primaryObject: string;
  typicalAction: string;
  permissions: MenuPermission[];
  keywords: string[];
}

export interface MenuArea {
  id: string;
  label: string;
  order: number;
  screens: MenuScreen[];
}

export interface MenuModule {
  id: string;
  code: string;
  label: string;
  description: string;
  icon: string;
  order: number;
  areas: MenuArea[];
}

export interface MenuStructure {
  version: string;
  lastUpdated: string;
  description: string;
  maxDepth: number;
  modules: MenuModule[];
}

export interface FlatMenuItem {
  id: string;
  label: string;
  description: string;
  route: string;
  icon: string;
  module: string;
  moduleLabel: string;
  area: string;
  areaLabel: string;
  permissions: MenuPermission[];
  keywords: string[];
  breadcrumb: string[];
}

class MenuService {
  private menuData: MenuStructure;
  private flatMenuCache: FlatMenuItem[] | null = null;
  private routeMapCache: Map<string, FlatMenuItem> | null = null;

  constructor() {
    this.menuData = menuStructure as MenuStructure;
  }

  /**
   * Get the complete menu structure
   */
  getMenuStructure(): MenuStructure {
    return this.menuData;
  }

  /**
   * Get all modules
   */
  getModules(): MenuModule[] {
    return this.menuData.modules.sort((a, b) => a.order - b.order);
  }

  /**
   * Get a specific module by ID
   */
  getModule(moduleId: string): MenuModule | undefined {
    return this.menuData.modules.find(m => m.id === moduleId);
  }

  /**
   * Get all screens from a module
   */
  getModuleScreens(moduleId: string): MenuScreen[] {
    const module = this.getModule(moduleId);
    if (!module) return [];

    const screens: MenuScreen[] = [];
    module.areas.forEach(area => {
      screens.push(...area.screens);
    });
    return screens;
  }

  /**
   * Flatten menu structure for easy searching and filtering
   */
  getFlatMenuItems(): FlatMenuItem[] {
    if (this.flatMenuCache) {
      return this.flatMenuCache;
    }

    const flatItems: FlatMenuItem[] = [];

    this.menuData.modules.forEach(module => {
      module.areas.forEach(area => {
        area.screens.forEach(screen => {
          flatItems.push({
            id: screen.id,
            label: screen.label,
            description: screen.description,
            route: screen.route,
            icon: screen.icon,
            module: module.id,
            moduleLabel: module.label,
            area: area.id,
            areaLabel: area.label,
            permissions: screen.permissions,
            keywords: screen.keywords,
            breadcrumb: [module.label, area.label, screen.label],
          });
        });
      });
    });

    this.flatMenuCache = flatItems;
    return flatItems;
  }

  /**
   * Get menu item by route
   */
  getMenuItemByRoute(route: string): FlatMenuItem | undefined {
    if (!this.routeMapCache) {
      this.routeMapCache = new Map();
      this.getFlatMenuItems().forEach(item => {
        this.routeMapCache!.set(item.route, item);
      });
    }
    return this.routeMapCache.get(route);
  }

  /**
   * Search menu items by keyword
   */
  searchMenuItems(query: string): FlatMenuItem[] {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const searchTerm = query.toLowerCase().trim();
    const flatItems = this.getFlatMenuItems();

    return flatItems.filter(item => {
      // Search in label
      if (item.label.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in description
      if (item.description.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in keywords
      if (item.keywords.some(kw => kw.toLowerCase().includes(searchTerm))) {
        return true;
      }

      // Search in module/area labels
      if (item.moduleLabel.toLowerCase().includes(searchTerm)) {
        return true;
      }

      if (item.areaLabel.toLowerCase().includes(searchTerm)) {
        return true;
      }

      return false;
    });
  }

  /**
   * Filter modules by user permissions
   */
  filterModulesByPermissions(
    modules: MenuModule[],
    userPermissions: string[]
  ): MenuModule[] {
    return modules
      .map(module => {
        // Filter areas
        const filteredAreas = module.areas
          .map(area => {
            // Filter screens
            const filteredScreens = area.screens.filter(screen =>
              this.hasPermission(screen.permissions, userPermissions)
            );

            if (filteredScreens.length === 0) {
              return null;
            }

            return {
              ...area,
              screens: filteredScreens,
            };
          })
          .filter((area): area is MenuArea => area !== null);

        if (filteredAreas.length === 0) {
          return null;
        }

        return {
          ...module,
          areas: filteredAreas,
        };
      })
      .filter((module): module is MenuModule => module !== null);
  }

  /**
   * Check if user has required permissions for a screen
   */
  hasPermission(
    requiredPermissions: MenuPermission[],
    userPermissions: string[]
  ): boolean {
    if (requiredPermissions.length === 0) {
      return true; // No permissions required
    }

    // Check if user has at least one of the required permissions
    return requiredPermissions.some(req => {
      return req.actions.some(action => {
        const permissionString = `${req.screen}.${action}`;
        return userPermissions.includes(permissionString);
      });
    });
  }

  /**
   * Get breadcrumb for a route
   */
  getBreadcrumb(route: string): string[] {
    const menuItem = this.getMenuItemByRoute(route);
    return menuItem?.breadcrumb || [];
  }

  /**
   * Get module icon by route
   */
  getModuleByRoute(route: string): MenuModule | undefined {
    const menuItem = this.getMenuItemByRoute(route);
    if (!menuItem) return undefined;
    return this.getModule(menuItem.module);
  }

  /**
   * Get all routes in the system
   */
  getAllRoutes(): string[] {
    return this.getFlatMenuItems().map(item => item.route);
  }

  /**
   * Get related screens (same module, different area)
   */
  getRelatedScreens(route: string, limit: number = 5): FlatMenuItem[] {
    const currentItem = this.getMenuItemByRoute(route);
    if (!currentItem) return [];

    const flatItems = this.getFlatMenuItems();
    return flatItems
      .filter(
        item =>
          item.module === currentItem.module &&
          item.route !== currentItem.route
      )
      .slice(0, limit);
  }

  /**
   * Get quick actions for a module (common create/list actions)
   */
  getQuickActions(moduleId: string): FlatMenuItem[] {
    const module = this.getModule(moduleId);
    if (!module) return [];

    const flatItems = this.getFlatMenuItems();
    return flatItems
      .filter(
        item =>
          item.module === moduleId &&
          (item.label.toLowerCase().includes('create') ||
            item.label.toLowerCase().includes('new') ||
            item.label.toLowerCase().includes('dashboard'))
      )
      .slice(0, 5);
  }

  /**
   * Get favorite-able routes (list/dashboard pages only)
   */
  getFavoriteableRoutes(): FlatMenuItem[] {
    const flatItems = this.getFlatMenuItems();
    return flatItems.filter(
      item =>
        !item.route.includes('/:') && // Exclude detail pages with params
        !item.route.includes('/new') && // Exclude create pages
        !item.route.includes('/edit') // Exclude edit pages
    );
  }
}

// Export singleton instance
export const menuService = new MenuService();
export default menuService;
